/**
 * FreeFortifyPanel — post-conquest army transfer UI.
 *
 * Shown immediately after a conquering attack (or as the first step of the
 * post-elim-placement interrupt) while `state.lastConquest` is set and the
 * source territory still has transferable armies.
 *
 * The player picks how many extra armies to slide from the conquering source
 * into the newly-taken territory. Defaulting to maxTransfer ("move the whole
 * stack") so the most-common action is one tap.
 *
 * Calling `onTransfer(0)` is the canonical "skip" — the backend clears
 * `lastConquest` on any armies value including 0.
 *
 * The panel renders nothing when maxTransfer === 0 (source has no moveable
 * armies); callers must decide what to show in that case.
 */

import React, { useState, useEffect } from 'react'
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native'
import { armyHeadroom } from '@warcarp/api-types'
import { usePostConquestTransfer } from '../../hooks/useGames'
import { useTheme } from '../../hooks/useTheme'
import { freeFortifyCopy } from '../../lib/freeFortifyCopy'
import type { GameState } from '../../lib/gameState'
import { TurnControlCard } from './TurnControlCard'
import { TURN_CARD_SPACING } from '../../lib/turnPanelAppearance'

export interface FreeFortifyPanelProps {
  gameId: string
  state: GameState
  /** Called after a successful transfer so callers can reset local state. */
  onTransferComplete?: () => void
  /**
   * Bonus armies waiting to be placed once the fortify window closes. When > 0
   * (the post-elimination interrupt) the panel makes the two-step sequence
   * explicit: fortify FIRST, then place the elimination bonus. Omit/0 for the
   * normal attack-phase post-conquest transfer.
   */
  bonusArmiesAfter?: number
}

export function FreeFortifyPanel({
  gameId,
  state,
  onTransferComplete,
  bonusArmiesAfter,
}: FreeFortifyPanelProps) {
  const transfer = usePostConquestTransfer(gameId)
  const copy = freeFortifyCopy(bonusArmiesAfter)
  const { theme } = useTheme()
  // Free-fortify accent. Light mode uses the darker burnt-amber (Tailwind
  // amber-700) so the border and the primary-button fill actually read
  // against the sand panel — bright amber-400 was ~1.28:1, effectively
  // invisible in light mode. Dark mode keeps the bright amber-400 that
  // already pops on the dark canvas. Applied inline (not via bg-amber-700 /
  // border color classes) so it renders regardless of NativeWind JIT class
  // generation. White button text on the light-mode fill is ~5.3:1 (WCAG AA),
  // mirroring the app's other primary buttons (light text on a colored fill).
  const accent = theme === 'dark' ? '#fbbf24' : '#b45309'

  const pendingConquest = state.lastConquest ?? null
  const transferSource = pendingConquest
    ? state.map.territories.find((t) => t.id === pendingConquest.fromTerritoryId) ?? null
    : null
  const transferTarget = pendingConquest
    ? state.map.territories.find((t) => t.id === pendingConquest.toTerritoryId) ?? null
    : null
  // Bounded by BOTH ends: what the source can spare, and what the destination
  // can still hold. The server rejects an over-cap transfer outright, so
  // offering armies it will refuse just hands the player a failed action.
  // Absent maxArmies = Infinity, which leaves uncapped maps exactly as before.
  const maxTransfer = transferSource
    ? Math.min(Math.max(0, transferSource.armies - 1), armyHeadroom(transferTarget ?? { armies: 0 }))
    : 0

  const [transferArmies, setTransferArmies] = useState(0)

  useEffect(() => {
    // Default to maxTransfer so "move the whole stack forward" is one tap.
    // Re-evaluate whenever the conquest pair or army counts change.
    if (!pendingConquest) return
    setTransferArmies(maxTransfer)
  }, [pendingConquest?.fromTerritoryId, pendingConquest?.toTerritoryId, maxTransfer])

  // Render nothing when there is nothing to transfer (source has exactly 1
  // army left after the conquest). Callers use this to fall through to the
  // next UI step immediately.
  if (!pendingConquest || !transferSource || !transferTarget || maxTransfer < 1) {
    return null
  }

  async function handleTransfer(armies: number = transferArmies) {
    if (!pendingConquest) return
    try {
      await transfer.mutateAsync({ armies })
      onTransferComplete?.()
    } catch {
      // surfaced via transfer.isError below
    }
  }

  return (
    <TurnControlCard
      className="gap-2"
      accent="transfer"
      accessibilityLabel={`Post-conquest transfer ${transferSource.name} → ${transferTarget.name}`}
      testID="turn-control-card"
    >
      <View className="flex-row items-center gap-2">
        <Text className="text-amber-700 dark:text-amber-400 text-xs font-stencil uppercase flex-1">
          Free fortify · {transferSource.name} → {transferTarget.name}
        </Text>
        <TouchableOpacity
          onPress={() => handleTransfer(0)}
          disabled={transfer.isPending}
          accessibilityLabel="Close free fortify"
          accessibilityRole="button"
          className="w-7 h-7 items-center justify-center"
          style={{ opacity: transfer.isPending ? 0.4 : 1 }}
        >
          <Text className="text-amber-700 dark:text-amber-400 text-lg font-stencil">×</Text>
        </TouchableOpacity>
      </View>
      <View className="flex-row items-center gap-1">
        <TouchableOpacity
          onPress={() => setTransferArmies(0)}
          disabled={transferArmies === 0 || transfer.isPending}
          accessibilityLabel="Transfer none"
          accessibilityRole="button"
          className="bg-paper border border-olive rounded-md items-center justify-center px-2"
          style={{
            height: 32,
            minWidth: 44,
            opacity: transferArmies === 0 || transfer.isPending ? 0.4 : 1,
          }}
        >
          <Text className="text-ink font-stencil text-xs uppercase">None</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setTransferArmies((a) => Math.max(0, a - 1))}
          disabled={transferArmies <= 0 || transfer.isPending}
          accessibilityLabel="Decrease armies"
          accessibilityRole="button"
          className="bg-paper border border-olive rounded-md items-center justify-center"
          style={{
            width: 32,
            height: 32,
            opacity: transferArmies <= 0 || transfer.isPending ? 0.4 : 1,
          }}
        >
          <Text className="text-ink font-stencil">−</Text>
        </TouchableOpacity>
        <Text
          className="flex-1 text-center text-ink text-base font-stencil"
          accessibilityLabel={`${transferArmies} armies to move`}
        >
          {transferArmies}
          <Text className="text-ink-soft text-xs font-typed"> / {maxTransfer}</Text>
        </Text>
        <TouchableOpacity
          onPress={() => setTransferArmies((a) => Math.min(maxTransfer, a + 1))}
          disabled={transferArmies >= maxTransfer || transfer.isPending}
          accessibilityLabel="Increase armies"
          accessibilityRole="button"
          className="bg-paper border border-olive rounded-md items-center justify-center"
          style={{
            width: 32,
            height: 32,
            opacity: transferArmies >= maxTransfer || transfer.isPending ? 0.4 : 1,
          }}
        >
          <Text className="text-ink font-stencil">+</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setTransferArmies(maxTransfer)}
          disabled={transferArmies === maxTransfer || transfer.isPending}
          accessibilityLabel="Transfer all available"
          accessibilityRole="button"
          className="bg-paper border border-olive rounded-md items-center justify-center px-2"
          style={{
            height: 32,
            minWidth: 44,
            opacity: transferArmies === maxTransfer || transfer.isPending ? 0.4 : 1,
          }}
        >
          <Text className="text-ink font-stencil text-xs uppercase">All</Text>
        </TouchableOpacity>
      </View>
      <TouchableOpacity
        onPress={() => handleTransfer()}
        disabled={transfer.isPending}
        accessibilityLabel={
          transferArmies === 0 ? 'Skip free fortify' : 'Move armies into conquered territory'
        }
        accessibilityRole="button"
        className="rounded-md py-1.5 items-center justify-center flex-row gap-1"
        style={{ backgroundColor: accent, opacity: transfer.isPending ? 0.4 : 1, minHeight: 32, marginTop: TURN_CARD_SPACING.placementActionsTop }}
      >
        {transfer.isPending && (
          <ActivityIndicator size="small" color={theme === 'dark' ? '#1a1a1a' : '#ffffff'} />
        )}
        <Text className="text-white dark:text-canvas font-stencil text-xs uppercase">
          {transferArmies === 0
            ? copy.skipLabel
            : `Move ${transferArmies} ${transferArmies === 1 ? 'army' : 'armies'}`}
        </Text>
      </TouchableOpacity>
      {transfer.isError && (
        <Text className="text-blood text-[10px] font-typed">
          {transfer.error instanceof Error ? transfer.error.message : 'Transfer failed'}
        </Text>
      )}
    </TurnControlCard>
  )
}
